import {
  Component,
  Element,
  Event,
  EventEmitter,
  Host,
  Prop,
  State,
  h,
} from '@stencil/core';
import { HTMLStencilElement } from '@stencil/core/internal';
import { Unsubscribe, store } from '@stencil/redux';
import { ANALYTICS_EVENTS } from '../../constants/analytics/rabo-scanner';
import { Language, SigningMethod, SourceName } from '../../enums/const-enums';
import { switchLanguage } from '../../redux/actions/config-actions';
import {
  setActiveMethod,
  setPreferredMethod,
} from '../../redux/actions/method-action';
import { RootState, initialStore } from '../../redux/store';
import {
  ApplicationData,
  EventName,
  sendEvent,
} from '../../service/piano-analytics';
import { SignCancel } from '../../service/sign-cancel';
import { SignInit } from '../../service/sign-init';
import { logd } from '../../utils/utils';
import {
  RassFooterTranslations,
  rassFooterTranslations,
} from './i18n/rass-footer.i18n';

type AllowedSwitchSignMethods =
  | SigningMethod.EOTP
  | SigningMethod.QR
  | SigningMethod.RABO_SCANNER
  | SigningMethod.RANDOM_READER
  | SigningMethod.TIN_WEB;

@Component({
  tag: 'rass-footer',
  styleUrl: 'rass-footer.scss',
  shadow: false,
})
export class RassFooter {
  @Element() element!: HTMLStencilElement;
  @Prop() tinWebRedirectUrl = '';
  @State() assetsPath: string;
  @State() sourceName: SourceName;
  @State() language: Language;
  @State() isSignInitialized: boolean;
  @State() activeSignMethod: SigningMethod;
  @State() allowedSignMethods: Array<AllowedSwitchSignMethods>;
  @State() allowedSwitchSignMethods: Array<AllowedSwitchSignMethods>;
  notAllowedSwitchSignMethods: Array<SigningMethod> = [
    SigningMethod.APP,
    SigningMethod.USERKEY_TIN_APP,
    SigningMethod.ONE_CLICK_ID,
  ];
  @Event({ eventName: 'footer:switchedSignMethod' })
  switchedSignMethodEvent: EventEmitter<SigningMethod>;
  iconMap = {
    [SigningMethod.RABO_SCANNER]: 'rds-icon-rabo-scanner',
    [SigningMethod.RANDOM_READER]: 'rds-icon-random-reader-comfort',
    [SigningMethod.QR]: 'rds-icon-qr-code',
    [SigningMethod.TIN_WEB]: 'rds-icon-person',
    [SigningMethod.EOTP]: 'rds-icon-envelope',
  };
  setActiveMethod!: typeof setActiveMethod;
  setPreferredMethod!: typeof setPreferredMethod;
  switchLanguage!: typeof switchLanguage;
  unsubscribe!: Unsubscribe;
  iframeParentWithoutFooter = [
    '/business/overview',
    '/business/service/security/access-and-signing/change-access-code',
    '/online/nl/profiel/toegang-en-ondertekenen',
    '/online/nl/pre-registratie/voorwaarden',
  ];

  componentWillLoad(): void {
    store.setStore(initialStore);
    store.mapDispatchToProps(this, {
      setActiveMethod,
      setPreferredMethod,
      switchLanguage,
    });
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      const {
        config: { assetsPath, language, sourceName, isSignInitialized },
        methods: { activeSignMethod, allowedSignMethods },
      } = state;
      return {
        assetsPath,
        language,
        sourceName,
        isSignInitialized,
        activeSignMethod,
        allowedSignMethods,
      };
    });
  }

  componentWillRender(): void {
    this.allowedSwitchSignMethods = this.allowedSignMethods.filter(
      (obj) => !this.notAllowedSwitchSignMethods.includes(obj),
    );
  }

  sendAnalyticsEvent(name: EventName, event: ApplicationData): void {
    sendEvent(name, event).catch((err: Error) =>
      logd(() => console.error('Error while sending analytics event:', err)),
    );
  }

  handleSwitchSignMethod(selectedSignMethod: AllowedSwitchSignMethods): void {
    logd(() => console.log('Switching to method:', selectedSignMethod));

    if (
      this.sourceName === SourceName.LOGIN &&
      SigningMethod.RANDOM_READER === selectedSignMethod
    ) {
      this.sendAnalyticsEvent(
        'click.action',
        ANALYTICS_EVENTS.EVENT_BUTTON_RANDOM_READER,
      );
    }
    if (
      SigningMethod.TIN_WEB === selectedSignMethod &&
      this.sourceName === SourceName.LOGIN
    ) {
      new SignCancel()
        .doSignCancel()
        .then(() => {
          this.redirect(this.tinWebRedirectUrl);
        })
        .catch((err: Error) => logd(() => console.error(err)));
      return;
    }
    if (
      selectedSignMethod !== SigningMethod.EOTP &&
      this.language !== Language.NL
    ) {
      this.switchLanguage(Language.EN);
    }
    this.setPreferredMethod(selectedSignMethod);
    if (!this.isSignInitialized) {
      new SignInit()
        .doSignInit()
        .catch((err: Error) => logd(() => console.error(err)));
    } else if (this.allowedSignMethods.includes(selectedSignMethod)) {
      this.setActiveMethod(selectedSignMethod);
    }
  }

  redirect(url: string): void {
    (window.parent ? window.parent : window).location.href = url;
    return;
  }

  getButtonLabel(signingMethod: AllowedSwitchSignMethods): string {
    const upperLabel = this.translations.button.label.up;
    return `${upperLabel.left[this.sourceName]} ${upperLabel.right[signingMethod]}
                <br/> ${this.translations.button.label.down[signingMethod]}`;
  }

  get isSectionInfoShown(): boolean {
    return (
      this.sourceName !== SourceName.IDIN &&
      this.activeSignMethod === SigningMethod.RANDOM_READER
    );
  }

  get isSectionMethodSwitchShown(): boolean {
    return (
      this.allowedSwitchSignMethods.filter(
        (signingMethod) => signingMethod !== this.activeSignMethod,
      ).length !== 0
    );
  }

  get translations(): RassFooterTranslations {
    return rassFooterTranslations[this.language] || rassFooterTranslations.en;
  }

  get isIframeParentWithoutFooter(): boolean {
    try {
      // Use a try...catch block because cross-origin usage of top.location will throw an err
      if (
        window.self !== window.top &&
        this.iframeParentWithoutFooter.includes(window.top.location.pathname)
      ) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  get isFooterHidden(): boolean {
    // Allow the footer to be hidden when only the "Help with" button is shown
    return (
      !this.isSectionInfoShown &&
      !this.isSectionMethodSwitchShown &&
      this.isIframeParentWithoutFooter
    );
  }

  onHelpClick(): void {
    this.sendAnalyticsEvent('click.action', ANALYTICS_EVENTS.EVENT_BUTTON_HELP);
  }

  render(): void {
    return (
      <Host>
        <style>@import url({`'${this.assetsPath}css/sfc-style.css'`});</style>
        <div
          class={`section-footer sfc-container-fluid ${this.isFooterHidden ? 'sfc-display-none' : ''}`}
          role="navigation"
        >
          <div
            class={
              this.isSectionInfoShown
                ? 'sfc-row sfc-justify-content-center sfc sfc-padding-block-end-200'
                : 'sfc-display-none'
            }
          >
            <div class="sfc-col-xs-6 sfc-col-sm-10 sfc-col-md-10 sfc-col-lg-10 sfc-col-xl-6">
              <p
                id="method-info"
                class="sfc-paragraph sfc-font-text-align-center sfc-padding-inline-100"
                innerHTML={this.translations.info[SigningMethod.RANDOM_READER]}
              />
            </div>
          </div>
          <div
            class={
              this.isSectionMethodSwitchShown
                ? 'section-method-switch sfc-display-flex sfc-justify-content-center sfc-padding-block-end-100'
                : 'sfc-display-none'
            }
          >
            {this.allowedSwitchSignMethods
              .filter(
                (signingMethod) => signingMethod !== this.activeSignMethod,
              )
              .map(
                (signingMethod, index) =>
                  (
                    <div class="signing-method-button" key={index}>
                      <sfc-button
                        class="sfc-margin-block-end-100"
                        variant="secondary"
                        id={`token-${signingMethod}`}
                        onSfcClick={(): void =>
                          this.handleSwitchSignMethod(signingMethod)
                        }
                      >
                        <div class="button-content sfc-display-flex">
                          <sfc-icon
                            name={this.iconMap[signingMethod]}
                            size="md"
                            class="sfc-display-block sfc-padding-inline-start-100"
                          />
                          <span
                            innerHTML={this.getButtonLabel(signingMethod)}
                            class="sfc-display-inline sfc-padding-inline-100 sfc-font-text-align-start"
                          />
                        </div>
                      </sfc-button>
                    </div>
                  ) as void,
              )}
          </div>
          <div class="sfc-row sfc-justify-content-center sfc-paragraph">
            <a
              id="cta-link"
              class="sfc-link sfc-text-link--inline"
              href={this.translations.cta.href[this.sourceName]}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(): void => this.onHelpClick()}
            >
              {this.translations.cta.text[this.sourceName]}
            </a>
          </div>
        </div>
      </Host>
    ) as void;
  }
}
